import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiBookOpen, FiUsers, FiCalendar, FiRefreshCcw } from 'react-icons/fi';
import apiService from '../../services/api';
// Reuse the My Courses styling so the two pages stay visually identical.
import styles from '../courses/Courses.module.css';

function Electives() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [electives, setElectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [authError, setAuthError] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const fetchElectives = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoading(true);
      }

      console.log('🔄 Fetching electives...');
      const response = await apiService.getElectives();
      console.log('📚 Electives response:', response);

      if (response.success && response.electives) {
        console.log(`✅ Loaded ${response.electives.length} electives`);
        setElectives(response.electives);
        setRetryCount(0);
        setAuthError(false);
        setFetchError(false);
      } else {
        console.warn('⚠️ No electives found in response:', response);
        setElectives([]);
        setFetchError(false);
      }
    } catch (error) {
      console.error('❌ Error fetching electives:', error);

      if (error.status === 401 || error.status === 403) {
        console.log('🔐 Authentication error detected, forcing logout - no retries');
        setElectives([]);
        setRetryCount(0);
        setAuthError(true);
        return;
      } else {
        setFetchError(true);
        if (retryCount < 2 && !isRefresh) {
          console.log(`🔄 Retrying elective fetch (attempt ${retryCount + 1}/2) in ${1000 * (retryCount + 1)}ms...`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchElectives(false);
          }, 1000 * (retryCount + 1));
        } else {
          console.error('❌ Max retries reached, giving up');
          setElectives([]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchElectives();
  }, [fetchElectives]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchElectives(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, fetchElectives, navigate, location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (electives.length > 0) {
        console.log('🔄 Periodic refresh of electives...');
        fetchElectives(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchElectives, electives.length]);

  const handleElectiveClick = (electiveId) => {
    navigate(`/electives/${electiveId}`);
  };

  const handleManualRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    setRetryCount(0);
    setAuthError(false);
    setFetchError(false);
    fetchElectives(false);
  };

  const formatStartDate = (elective) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Ended: the elective's end date is in the past.
    if (elective.end_date) {
      const end = new Date(elective.end_date);
      const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      if (endDate < today) return 'Ended';
    }

    if (!elective.start_date) return 'TBD';

    const date = new Date(elective.start_date);
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Started but not yet ended.
    if (startDate <= today) return 'Started';

    // Upcoming — show the start date.
    const options = {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getInstructorNames = (elective) =>
    elective.instructors && Array.isArray(elective.instructors) && elective.instructors.length > 0
      ? elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(', ')
      : `${elective.instructor_first_name || ''} ${elective.instructor_last_name || ''}`.trim();

  const filteredElectives = electives.filter(elective => {
    const instructorNames = getInstructorNames(elective);

    const matchesSearch = elective.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         elective.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (elective.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         instructorNames.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.heroRow}>
          <header className={styles.hero}>
            <div className={styles.heroEyebrow}>
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <h1 className={styles.heroTitle}>My Electives</h1>
            <p className={styles.heroSub}>Browse your enrolled electives.</p>
          </header>
          {fetchError && !loading && (
            <button className={styles.refreshButton} onClick={handleManualRefresh}>
              <FiRefreshCcw size={16} />
              Refresh
            </button>
          )}
        </div>
        <div className={styles.searchBar}>
          <div className={styles.searchIcon}>
            <FiSearch size={16} />
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search electives..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <FiBookOpen size={32} />
          <p>Loading electives...</p>
        </div>
      ) : filteredElectives.length > 0 ? (
        <div className={styles.coursesGrid}>
          {filteredElectives.map((elective) => {
            const instructorNames = getInstructorNames(elective);
            return (
              <div
                key={elective.id}
                className={styles.courseCard}
                onClick={() => handleElectiveClick(elective.id)}
              >
                <div className={styles.courseHeader}>
                  <h3 className={styles.courseTitle}>{elective.name}</h3>
                  <div className={styles.metaRow}>
                    <span className={styles.courseCode}>{elective.code}</span>
                    {instructorNames && (
                      <>
                        <span className={styles.metaSeparator}>•</span>
                        <span className={styles.courseInstructor}>{instructorNames}</span>
                      </>
                    )}
                  </div>
                  <div className={styles.statusRow}>
                    <div className={styles.statusBadge}>
                      <FiUsers size={12} />
                      <span>{elective.enrolled_students || 0} students</span>
                    </div>
                    <div className={styles.statusBadge}>
                      <FiCalendar size={12} />
                      <span>{formatStartDate(elective)}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.courseContent}>
                  <p className={styles.courseDescription}>{elective.description}</p>
                  <div className={styles.courseActions}>
                    <button className={styles.actionButton}>View Elective</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <FiBookOpen />
          <h3>No electives found</h3>
          {authError ? (
            <p>Your session has expired. You will be redirected to login shortly.</p>
          ) : (
            <p>Try adjusting your search to find what you're looking for.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Electives;
